import { MelodyLib } from '@tunasong/music-lib'
import type { Melody, MelodyChordLane, MelodyEntityNote } from '@tunasong/schemas'

/**
 * Edits on the selected notes that are not options, so they never go through the ghost layer (melody toolkit M6; Joni's
 * layout, 2026-10-31): Longer and Shorter, a grid step each, like dragging an edge. And the slice Loop plays while more
 * than one note is selected (D6).
 */

const endOf = (note: MelodyEntityNote) => note.onset + note.duration

/** Where the note after `index` starts, in the order the notes sound; Infinity when it is the last */
const nextOnset = (melody: Melody, index: number) => {
  const note = melody.notes[index]
  if (!note) {
    return Infinity
  }
  return melody.notes.reduce(
    (earliest, other, otherIndex) =>
      otherIndex !== index && other.onset > note.onset + MelodyLib.GRID_TOLERANCE
        ? Math.min(earliest, other.onset)
        : earliest,
    Infinity
  )
}

const longer = (melody: Melody, index: number, note: MelodyEntityNote) =>
  Math.min(note.duration + MelodyLib.gridSlotBeats(melody.grid), nextOnset(melody, index) - note.onset)

/** Longer: a grid step more, stopping at the next note's start in the line */
export const lengthenSelection = (melody: Melody, selection: number[]): Melody => ({
  ...melody,
  notes: melody.notes.map((note, index) =>
    selection.includes(index) ? { ...note, duration: Math.max(note.duration, longer(melody, index, note)) } : note
  ),
})

/** Whether Longer would lengthen any selected note */
export const canLengthen = (melody: Melody, selection: number[]) =>
  selection.some(index => {
    const note = melody.notes[index]
    return note !== undefined && longer(melody, index, note) > note.duration + MelodyLib.GRID_TOLERANCE
  })

/** Shorter: a grid step less, never below one */
export const shortenSelection = (melody: Melody, selection: number[]): Melody => {
  const slot = MelodyLib.gridSlotBeats(melody.grid)
  return {
    ...melody,
    notes: melody.notes.map((note, index) =>
      selection.includes(index) ? { ...note, duration: Math.max(slot, note.duration - slot) } : note
    ),
  }
}

/** Whether Shorter would shorten any selected note */
export const canShorten = (melody: Melody, selection: number[]) => {
  const slot = MelodyLib.gridSlotBeats(melody.grid)
  return selection.some(index => (melody.notes[index]?.duration ?? 0) > slot + MelodyLib.GRID_TOLERANCE)
}

export interface SelectionLoop {
  /** The bars the selection covers, moved to start at beat 0 */
  melody: Melody
  lane: MelodyChordLane
  /** Where those bars start in the melody: the play head is drawn this much later */
  startBeats: number
}

/**
 * D6: what Loop plays while more than one note is selected, the whole bars the selection covers, with every note and
 * chord sounding in them, cut at their edges. Null for one note or none: Loop then loops the whole melody.
 */
export const selectionLoop = (melody: Melody, lane: MelodyChordLane, selection: number[]): SelectionLoop | null => {
  const selected = selection.flatMap(index => melody.notes[index] ?? [])
  if (selected.length < 2) {
    return null
  }
  const bar = MelodyLib.barBeats(melody.tempo.meter)
  const start = Math.floor(Math.min(...selected.map(note => note.onset)) / bar + MelodyLib.GRID_TOLERANCE) * bar
  const end = Math.ceil(Math.max(...selected.map(endOf)) / bar - MelodyLib.GRID_TOLERANCE) * bar
  /** Each span cut to [start, end), moved to start at 0; null when it doesn't sound there */
  const cut = (onset: number, duration: number) => {
    const from = Math.max(onset, start)
    const to = Math.min(onset + duration, end)
    return to - from > MelodyLib.GRID_TOLERANCE ? { onset: from - start, duration: to - from } : null
  }
  return {
    melody: {
      ...melody,
      notes: melody.notes.flatMap(note => {
        const span = cut(note.onset, note.duration)
        return span ? [{ ...note, ...span }] : []
      }),
    },
    lane: {
      ...lane,
      chords: lane.chords.flatMap(chord => {
        const span = cut(chord.onset, chord.duration)
        return span ? [{ ...chord, ...span }] : []
      }),
    },
    startBeats: start,
  }
}
